import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/ui/dialog';
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

export interface FormData {
  id?: string;
  name: string;
  description: string;
  weightScore: number;
}

interface AddEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  initialData?: FormData | null;
  title?: string;
  submitButtonText?: string;
}

export const AddEditForm: React.FC<AddEditFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  title = "Add New Parameter",
  submitButtonText = "Add Parameter"
}) => {
  const [localFormData, setLocalFormData] = useState<FormData>({
    name: '',
    description: '',
    weightScore: 1
  });

  // Update local form data when props change
  useEffect(() => {
    if (initialData) {
      setLocalFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        weightScore: initialData.weightScore || 1
      });
    } else {
      setLocalFormData({
        name: '',
        description: '',
        weightScore: 1
      });
    }
  }, [initialData, isOpen]);

  // Form field definitions
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Parameter Name",
      placeholder: "Enter parameter name...",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter parameter description...",
      rows: 3,
      required: false,
      className: "col-span-1 mt-3",
    },
    {
      name: "weightScore",
      type: "slider",
      label: "Weight Score",
      min: 1,
      max: 5,
      step: 1,
      required: false,
      className: "col-span-1 mt-3",
      helperText: "Select weight score from 1 to 5",
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: localFormData,
  });

  // Reset form when localFormData changes
  useEffect(() => {
    form.reset(localFormData);
  }, [localFormData, form]);

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    const updatedData = {
      ...localFormData,
      [fieldName]: value,
    };
    setLocalFormData(updatedData);
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!localFormData.name.trim()) {
      return;
    }
    onSubmit(localFormData);
    handleClose();
  };

  // Handle form reset/cancel
  const handleClose = () => {
    setLocalFormData({
      name: '',
      description: '',
      weightScore: 1
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            onFieldChange={handleFieldChange}
            gridColumns={1}
            responsiveColumns={{
              sm: 1,
              md: 1,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: submitButtonText,
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer: "flex gap-2 justify-end pt-6 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                slider:
                  "w-full",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
                helperText: "text-sm text-gray-500 mt-1",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};